import { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Italic, List, ListOrdered, Heading2 } from 'lucide-react';

const isEmptyHtml = (html) => !html || html.replace(/<[^>]*>/g, '').trim() === '';

// Build simple HTML from a legacy bullet array (used when a card has no rich note yet).
export function bulletsToHtml(bullets) {
  const list = (bullets || []).filter(b => (b || '').trim());
  if (!list.length) return '';
  return `<ul>${list.map(b => `<li>${escapeHtml(b)}</li>`).join('')}</ul>`;
}

// Flatten rich HTML back into a plain-text bullet array so legacy consumers keep working.
export function htmlToBullets(html) {
  if (isEmptyHtml(html)) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const lis = Array.from(doc.querySelectorAll('li'));
  if (lis.length) return lis.map(li => li.textContent.trim()).filter(Boolean);
  return Array.from(doc.body.querySelectorAll('p'))
    .map(p => p.textContent.trim()).filter(Boolean);
}

function escapeHtml(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function ToolButton({ active, onClick, title, children, testid }) {
  return (
    <button type="button" title={title} data-testid={testid}
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      className={`rn-tb${active ? ' active' : ''}`}>
      {children}
    </button>
  );
}

export default function RichNote({ value, onSave, placeholder = 'Write a note…', editable = true, testid }) {
  const lastSaved = useRef(value || '');

  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] } })],
    content: value || '',
    editable,
    editorProps: { attributes: { class: 'rn-content', 'data-testid': testid || 'rich-note' } },
    onBlur: ({ editor }) => {
      const html = editor.isEmpty ? '' : editor.getHTML();
      if (html !== lastSaved.current) {
        lastSaved.current = html;
        onSave?.(html);
      }
    },
  });

  // Sync external value changes (e.g. after reload) without clobbering active edits.
  useEffect(() => {
    if (editor && (value || '') !== editor.getHTML() && !editor.isFocused) {
      editor.commands.setContent(value || '', false);
      lastSaved.current = value || '';
    }
  }, [value, editor]);

  if (!editor) return null;

  if (!editable) {
    return <div className="rn-readonly" data-testid={testid}><EditorContent editor={editor} /></div>;
  }

  return (
    <div className="rn-wrap">
      <div className="rn-toolbar">
        <ToolButton testid="rn-bold" title="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={14} /></ToolButton>
        <ToolButton testid="rn-italic" title="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={14} /></ToolButton>
        <span className="rn-div" />
        <ToolButton testid="rn-h2" title="Heading" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={14} /></ToolButton>
        <ToolButton testid="rn-bullet" title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={14} /></ToolButton>
        <ToolButton testid="rn-ordered" title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={14} /></ToolButton>
      </div>
      <EditorContent editor={editor} />
      {editor.isEmpty && <div className="rn-placeholder">{placeholder}</div>}
    </div>
  );
}
